import { useCallback, useEffect, useMemo } from "react";

import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import { getIn, useFormik } from "formik";
import enCountryLocale from "i18n-iso-countries/langs/en.json";
import isEqual from "lodash/isEqual";
import isNull from "lodash/isNull";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { formatPhoneNumberAsTyped } from "@waterly/utils-isomorphic";

import { CountryCodeSelect } from "../../pickers/CountryCodeSelect/CountryCodeSelect";

import {
  AddressPurpose,
  type AddressFieldName,
  type AddressFieldsConfig,
  type AddressFormValues,
  type CountryCodeKey,
} from "./Address.types";
import type { CountryLabels, SupportedLabelCountry } from "./address.utils";
import {
  MIN_SUPPORTED_PHONE_NUMBER,
  MAX_SUPPORTED_PHONE_NUMBER,
  regionSchema,
  useAddressCountryLabels,
} from "./address.utils";

const countryCodeName = "countryCode";

const phoneRegex = /^\+[1-9]\d{1,14}$/;

export type AddressProps = {
  value: AddressFormValues;
  onChange: (value: AddressFormValues) => void;
  disabled?: boolean;
  fieldsConfig?: AddressFieldsConfig;
  textFieldProps?: Omit<TextFieldProps, "name" | "value" | "onChange" | "onBlur" | "label" | "select">;
};

const isFieldVisible = (fieldsConfig: AddressFieldsConfig | undefined, name: AddressFieldName) =>
  fieldsConfig?.[name]?.visible !== false;

// if a field is hidden, it cannot be required
const isFieldRequired = (fieldsConfig: AddressFieldsConfig | undefined, name: AddressFieldName) =>
  isFieldVisible(fieldsConfig, name) && fieldsConfig?.[name]?.required === true;

const isFieldDisabled = (
  fieldsConfig: AddressFieldsConfig | undefined,
  name: AddressFieldName,
  disabled: boolean,
) => disabled || fieldsConfig?.[name]?.disabled === true;

const Address = ({ value, onChange, disabled = false, fieldsConfig, textFieldProps }: AddressProps) => {
  const { t } = useTranslation(["address", "common", "AddressPurposeEnum", "yup"]);

  const addressCountryLabels = useAddressCountryLabels();

  const countryLabels = useCallback(
    (selectedCountry: CountryCodeKey | null): CountryLabels => {
      const supportedCountries = Object.keys(addressCountryLabels) as SupportedLabelCountry[];
      if (isNull(selectedCountry) || !supportedCountries.includes(selectedCountry as SupportedLabelCountry)) {
        return addressCountryLabels["OTHER"];
      }
      return addressCountryLabels[selectedCountry as SupportedLabelCountry];
    },
    [addressCountryLabels],
  );

  const validationSchema = useMemo(() => {
    const stringField = (name: AddressFieldName) => {
      const schema = yup.string().nullable();
      return isFieldRequired(fieldsConfig, name) ? schema.required(t("yup:required")) : schema;
    };

    return yup.object({
      label: stringField("label"),
      recipientName: stringField("recipientName"),
      line1: stringField("line1"),
      line2: stringField("line2"),
      phone: yup
        .string()
        .nullable()
        .test("is-valid-phone", t("yup:invalid_phone"), (value) => {
          if (!value) return true;
          if (!phoneRegex.test(value)) return false;
          return value.length >= MIN_SUPPORTED_PHONE_NUMBER && value.length <= MAX_SUPPORTED_PHONE_NUMBER;
        }),
      city: stringField("city"),
      region: regionSchema(t, isFieldRequired(fieldsConfig, "region")),
      postalCode: stringField("postalCode"),
      countryCode: yup
        .string()
        .required(t("yup:required"))
        .test("valid-country-code", t("yup:invalid_country_code"), (value) => {
          if (!value) return false;
          return Object.keys(enCountryLocale.countries).includes(value.toUpperCase());
        }),
      purpose: yup.string().oneOf(Object.values(AddressPurpose)).required(t("yup:required")),
    }) as yup.SchemaOf<AddressFormValues>;
  }, [fieldsConfig, t]);

  const formik = useFormik<AddressFormValues>({
    initialValues: value,
    validationSchema,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  useEffect(() => {
    if (!isEqual(formik.values, value)) {
      onChange(formik.values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  const purposeOptions = useMemo(() => Object.values(AddressPurpose), []);
  const sharedTextFieldProps = {
    fullWidth: true,
    size: "small" as const,
    ...textFieldProps,
  };

  const getFieldState = (name: AddressFieldName) => {
    const error = Boolean(getIn(formik.touched, name) && getIn(formik.errors, name));
    const errorText = getIn(formik.touched, name) ? (getIn(formik.errors, name) as string) : undefined;
    const configHelperText = fieldsConfig?.[name]?.helperText;

    return { error, helperText: errorText ?? configHelperText };
  };

  const getFieldValue = (name: string) => {
    const field = formik.getFieldProps(name);
    return {
      ...field,
      value: (field.value as string) ?? "",
    };
  };

  const countryCodeValue = (getIn(formik.values, countryCodeName) as CountryCodeKey | null) ?? null;
  const handleCountryCodeChange = useCallback(
    async (value: CountryCodeKey | null) => {
      await formik.setFieldValue(countryCodeName, value ?? "");
      // to trigger validation and error state update on region when country changes, since some countries require region and others don't
      await formik.setFieldValue("region", formik.values.region);
      await formik.setFieldTouched("region", true, true);
      await formik.validateForm();
    },
    [formik],
  );

  const handlePhoneChange = useCallback<Required<TextFieldProps>["onChange"]>(
    (e) => {
      const input = e.target.value;
      // Allow only digits, prepend + if missing, max 16 chars (E.164 format)
      const digitsOnly = input.replace(/[^\d]/g, "");
      // Empty phone must be null (not "") to satisfy DB phone check constraints.
      const sanitized: AddressFormValues["phone"] = digitsOnly
        ? `+${digitsOnly}`.slice(0, MAX_SUPPORTED_PHONE_NUMBER)
        : null;
      void formik.setFieldValue("phone", sanitized);
    },
    [formik],
  );

  // these are convenience functions for checking field visibility and requirement dynamically
  const show = useCallback((name: AddressFieldName) => isFieldVisible(fieldsConfig, name), [fieldsConfig]);
  const required = useCallback(
    (name: AddressFieldName) => isFieldRequired(fieldsConfig, name),
    [fieldsConfig],
  );
  const fieldDisabled = useCallback(
    (name: AddressFieldName) => isFieldDisabled(fieldsConfig, name, disabled),
    [disabled, fieldsConfig],
  );

  return (
    <Stack spacing={2}>
      {show("label") && (
        <TextField
          required={required("label")}
          disabled={fieldDisabled("label")}
          {...sharedTextFieldProps}
          {...getFieldValue("label")}
          {...getFieldState("label")}
          label={t("address:address_label")}
          name="label"
          autoComplete="address-label"
        />
      )}
      {show("recipientName") && (
        <TextField
          required={required("recipientName")}
          disabled={fieldDisabled("recipientName")}
          {...sharedTextFieldProps}
          {...getFieldValue("recipientName")}
          {...getFieldState("recipientName")}
          label={t("address:recipient_name")}
          name="recipientName"
          autoComplete="name"
        />
      )}
      {show("line1") && (
        <TextField
          required={required("line1")}
          disabled={fieldDisabled("line1")}
          {...sharedTextFieldProps}
          {...getFieldValue("line1")}
          {...getFieldState("line1")}
          label={t("address:address_line_1")}
          name="line1"
          autoComplete="address-line1"
        />
      )}
      {show("line2") && (
        <TextField
          required={required("line2")}
          disabled={fieldDisabled("line2")}
          {...sharedTextFieldProps}
          {...getFieldValue("line2")}
          {...getFieldState("line2")}
          label={t("address:address_line_2")}
          name="line2"
          autoComplete="address-line2"
        />
      )}
      <CountryCodeSelect
        {...sharedTextFieldProps}
        {...getFieldState(countryCodeName)}
        disabled={fieldDisabled("countryCode")}
        label={t("address:country")}
        name={countryCodeName}
        value={countryCodeValue}
        onChange={handleCountryCodeChange}
        onBlur={formik.handleBlur}
        required
        fullWidth
        autoComplete="country"
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {show("city") && (
          <TextField
            required={required("city")}
            disabled={fieldDisabled("city")}
            {...sharedTextFieldProps}
            {...getFieldValue("city")}
            {...getFieldState("city")}
            label={countryLabels(countryCodeValue).cityLabel}
            name="city"
            autoComplete="address-level2"
          />
        )}
        {show("postalCode") && (
          <TextField
            required={required("postalCode")}
            disabled={fieldDisabled("postalCode")}
            {...sharedTextFieldProps}
            {...getFieldValue("postalCode")}
            {...getFieldState("postalCode")}
            label={countryLabels(countryCodeValue).postalCodeLabel}
            name="postalCode"
            autoComplete="postal-code"
          />
        )}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <TextField
          required={required("region")}
          disabled={fieldDisabled("region")}
          {...sharedTextFieldProps}
          {...getFieldValue("region")}
          {...getFieldState("region")}
          label={countryLabels(countryCodeValue).regionLabel}
          name="region"
          autoComplete="address-level1"
        />
        {show("phone") && (
          <TextField
            disabled={fieldDisabled("phone")}
            {...sharedTextFieldProps}
            {...getFieldValue("phone")}
            {...getFieldState("phone")}
            // a display template for the address country, the change handler strips it back to the raw digits we store
            value={formatPhoneNumberAsTyped(getFieldValue("phone").value, countryCodeValue)}
            type="tel"
            label={t("common:phone_number")}
            name="phone"
            onChange={handlePhoneChange}
            autoComplete="tel"
          />
        )}
      </Box>
      {show("purpose") && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            required
            disabled={fieldDisabled("purpose")}
            {...sharedTextFieldProps}
            {...getFieldValue("purpose")}
            {...getFieldState("purpose")}
            label={t("address:address_purpose")}
            name="purpose"
            select
            autoComplete="off"
          >
            {purposeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {t(`AddressPurposeEnum:${option}`)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
    </Stack>
  );
};

export default Address;
